/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */ import clsx from 'clsx'
import type { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import NextImage from 'next/legacy/image'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { RespondWithNotFound, serverPropsHandler } from '../../libs/next/api/handlers'
import { websiteContent } from '../../libs/next/loaders/graphql/src/contentClient'
import { Content } from '../../src/components/content/Content'
import { AdContent } from '../../src/components/shared/AdContent'
import { Button } from '../../src/components/shared/Button'
import { Container } from '../../src/components/shared/Container'
import { Hidden } from '../../src/components/shared/Hidden'
import { Layout } from '../../src/components/shared/Layout'
import { ProductCarousel } from '../../src/components/shared/ProductCarousel'
import { ProductEmptyState } from '../../src/components/shared/ProductEmptyState'
import { ReferenceGroup } from '../../src/components/shared/ReferenceGroup'
import { SuggestionModal } from '../../src/components/shared/SuggestionModal'
import { TagRow } from '../../src/components/shared/TagRow'
import { InternalLink } from '../../src/const/Links'
import { setSeoIngredient } from '../../src/const/setSeo'
import { Translation, useTranslate } from '../../src/contexts/TranslationContext'
import { AdFragment } from '../../src/data/AdFragment'
import { DidYouKnowFragment } from '../../src/data/DidYouKnowFragment'
import { ItemDetailFragment } from '../../src/data/ItemFragment'
import { LayoutFragment } from '../../src/data/LayoutFragment'
import { unserializeFilter } from '../../src/filter/unserializeFilter'
import { loadDeclensionCategoryProductsDogIgnored } from '../../src/loaders/loadDeclensionCategoryProducts'
import style from '../../src/styles/IngredientPageDetail.module.sass'
import { isLocalUrl } from '../../src/utils/isLocalUrl'
import { roundTheCurrentTimeToMinutes } from '../../src/utils/roundTheCurrentTimeToMinutes'
import { useDidYouKnowCheck } from '../../src/utils/useDidYouKnowCheck'

type GenericPageProps = InferGetServerSidePropsType<typeof getServerSideProps>

export default function GenericPage(props: GenericPageProps) {
	const [isModalOpen, setIsModalOpen] = useState(false)

	const item = props.data.getItem
	const referenceList = props.data.getItem?.referenceList
	const breadCrumbsItems = [
		{
			id: item?.categories[0]?.slug as string,
			title: item?.categories[0]?.name as string,
			link: `${InternalLink.IngredientCategory}${item?.categories[0]?.slug}`,
		},
	]

	const [referenceOpen, setReferenceOpen] = useState(false)

	const translate = useTranslate()

	const ingredientDeclensionArray = item?.declensionFourth?.label?.split(' ')
	const restDeclension = ingredientDeclensionArray?.splice(1)
	const stringOfDeclension = restDeclension?.join(' ')
	const isNegative = ingredientDeclensionArray && ingredientDeclensionArray[0].startsWith('ne')

	const router = useRouter()
	const { preview } = router.query

	const image = (
		<div className={style.imageWrapper}>
			<NextImage
				src={item?.image?.url ?? '/images/placeholder.webp'}
				alt={translate('alternativeText.ingredient')}
				title={item?.name}
				layout="fill"
				priority
				objectFit="contain"
			/>
		</div>
	)

	return (
		<Layout
			didYouKnow={useDidYouKnowCheck(props.data.listDidYouKnow)}
			hasBreadcrumbs
			breadcrumbsItems={breadCrumbsItems}
			footer={props.data.footer.getFooter}
			socials={props.data.socials.listSocial}
			seo={setSeoIngredient(item)}
			url={`${InternalLink.Ingredient}${props.data.getItem?.slug}`}
		>
			<SuggestionModal isModalOpen={isModalOpen} handleClose={() => setIsModalOpen(false)} />
			<Container size="wide">
				<div className={style.container}>
					<div className={style.contentLeft}>
						<Hidden breakpoint="desktopDown">{image}</Hidden>
						<AdContent ads={props.data.listAd ?? []} isAlignedRight withContentAbove />{' '}
					</div>

					<Container size="narrow" disableGutters>
						<Hidden breakpoint="desktopUp">{image}</Hidden>
						<h1 className={style.title}>{item?.name}</h1>
						{(item?.positiveForBodyTags || item?.negativeForBodyTags) && (
							<div className={style.articleTagsGroup}>
								{item.positiveForBodyTags.length > 0 && (
									<div className={style.article}>
										<div className={style.flavorTitle}>
											<Translation tr="ingredient.positiveBodyTags.title" />
										</div>
										<div className={style.rowWrapper}>
											<TagRow tags={item.positiveForBodyTags} />
										</div>
									</div>
								)}
								{item.negativeForBodyTags.length > 0 && (
									<div className={style.article}>
										<div className={style.flavorTitle}>
											<Translation tr="ingredient.negativeBodyTags.title" />
										</div>
										<div className={style.rowWrapper}>
											<TagRow tags={item.negativeForBodyTags} />
										</div>
									</div>
								)}
							</div>
						)}
						<div
							className={style.contentText}
							onClick={(e) => {
								const element = e.target as HTMLLinkElement
								if (element.tagName.toLowerCase() === 'sup') {
									setReferenceOpen(true)
								}
								if (element.tagName.toLowerCase() === 'a') {
									if (
										!isLocalUrl(element.getAttribute('href') ?? '') ||
										element.getAttribute('href')?.includes(InternalLink.Ingredient)
									) {
										element.setAttribute('target', '_blank')
									}
								}
							}}
						>
							{item?.content?.blocks ? (
								<Content blocks={preview ? (item?.draft?.blocks ?? []) : (item?.content?.blocks ?? [])} />
							) : (
								<span className={style.lead}>{item?.lead}</span>
							)}
						</div>
						{referenceList?.references && referenceList.references.length > 0 && (
							<ReferenceGroup
								referenceList={referenceList}
								isOpen={referenceOpen}
								close={() => setReferenceOpen(false)}
							/>
						)}
						{props.relatedProducts.totalCount && props.relatedProducts.totalCount > 0 ? (
							item && (
								<ProductCarousel
									buttonLink={`${InternalLink.Results}/${item.declensionFourth?.slug}`}
									ingredientDeclension={item?.declensionFourth?.label}
									products={props.relatedProducts.items}
									productAmount={props.relatedProducts.totalCount}
								/>
							)
						) : (
							<div className={style.emptyStateWrapper}>
								<ProductEmptyState
									title={
										<>
											<Translation tr="ingredient.emptyState.title" />{' '}
											<span className={isNegative ? style.greenColor : ''}>{ingredientDeclensionArray?.[0]} </span>
											<span className={clsx(style.colorGreen, style.lowercase)}>{stringOfDeclension}</span>
										</>
									}
									subtitle={
										<span className={style.emptyStateButton}>
											<Translation tr="ingredient.emptyState.subtitle.first" />{' '}
											<Button
												type={{ variant: 'underlined' }}
												label={translate('ingredient.emptyState.button')}
												onClick={() => setIsModalOpen(true)}
											/>{' '}
											<Translation tr="emptyState.subtitle.second" />
										</span>
									}
								/>
							</div>
						)}
					</Container>
				</div>
			</Container>
		</Layout>
	)
}

// export const runtime = 'experimental-edge'
export const getServerSideProps = serverPropsHandler(async (context: GetServerSidePropsContext) => {
	context.res.setHeader('Cache-Control', 's-maxage=60, stale-while-revalidate=3600')

	const content = websiteContent

	const itemSlug = String(context.query.ingredient_slug)

	const data = await content('query')({
		...LayoutFragment(),
		listAd: [
			{
				filter: {
					isActive: { eq: true },
					activeFrom: { lte: roundTheCurrentTimeToMinutes() },
					activeUntil: { gte: roundTheCurrentTimeToMinutes() },
					or: [{ isIngredientSectionVisible: { eq: true } }],
				},
			},
			AdFragment(),
		],
		getItem: [
			{
				by: {
					slug: itemSlug,
				},
			},

			ItemDetailFragment(),
		],
		listDidYouKnow: [
			{
				limit: 40,
				orderBy: [
					{
						_random: true,
					},
				],
			},
			DidYouKnowFragment(),
		],
	})

	const getItem = data.getItem

	if (!getItem) {
		throw new RespondWithNotFound('Složka nenalezena')
	}

	const serializedFilter = String(context.query.filter ?? '{}')
	const initialFilter = unserializeFilter(serializedFilter)

	return {
		props: {
			data,
			relatedProducts: await loadDeclensionCategoryProductsDogIgnored(getItem.declensionFourth, 8),
			initialFilter,
		},
	}
})
